import React, { useEffect, useRef } from 'react';
import { MapContainer, Marker, Popup, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { hasFeature } from '../utils/featureFlags';
import { createBaseLayer, createSeamarkLayer } from '../utils/basemap';

/**
 * 海しる（海洋状況表示システム）の出典表記。
 * 海洋状況表示システム利用規約 2-1) が出典の記載を求めており、GeoJSONを独自の記号・色で
 * 描き直している＝編集・加工に当たるため「加工して作成」も併記する。
 * msil_attribution フラグが有効な会社でのみ表示する（まずユニゾンで検証してから全社展開）。
 */
const MSIL_ATTRIBUTION =
  '出典：<a href="https://www.msil.go.jp/" target="_blank" rel="noopener noreferrer">海しる（海洋状況表示システム）</a>を加工して作成';

export interface TrailPoint {
  ts: number;
  lat: number;
  lng: number;
  speed: number;
  heading: number;
}

export interface VesselData {
  id: string;
  name: string;
  code: string;
  status: string;
  trail: TrailPoint[];
  color: string;
}

export interface PortMarker {
  id: string;
  name: string;
  code?: string;
  latitude: number;
  longitude: number;
  description?: string;
}

export type MapType = 'standard' | 'satellite';

interface Props {
  vessels: VesselData[];
  selectedId: string | null;
  currentTime: number;
  showTrail: boolean;
  showLabel: boolean;
  mapType?: MapType;
  showMaritimeZones?: boolean;
  apiBase?: string;
  msilLayers?: string[];
  ports?: PortMarker[];
  /** Windy/AIS などのオーバーレイ表示中に、ズームボタン・凡例を隠す */
  hideUi?: boolean;
}

// 運航状態は打刻・GPSから自動判定される（荷役→入港中 / 航海・当直 or 移動中→航行中 / 動きなし→停泊中 / GPS途絶→オフライン）
const STATUS_COLOR = { sailing: '#0ea5e9', anchored: '#94a3b8', port: '#22c55e', offline: '#64748b' };
const STATUS_LABEL: Record<string, string> = {
  sailing: '航行中', port: '入港中', anchored: '停泊中', offline: 'オフライン',
};

function interpolatePosition(trail: TrailPoint[], ts: number): { lat: number; lng: number; heading: number } | null {
  if (trail.length === 0) return null;
  const sorted = [...trail].sort((a, b) => a.ts - b.ts);
  const first = sorted[0];
  const last  = sorted[sorted.length - 1];
  if (!first || !last) return null;
  if (ts <= first.ts) return { lat: first.lat, lng: first.lng, heading: first.heading };
  if (ts >= last.ts)  return { lat: last.lat,  lng: last.lng,  heading: last.heading };
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (!a || !b) continue;
    if (ts >= a.ts && ts <= b.ts) {
      const t = (ts - a.ts) / (b.ts - a.ts);
      return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t, heading: a.heading };
    }
  }
  return { lat: last.lat, lng: last.lng, heading: last.heading };
}

function createShipIcon(color: string, heading: number, selected: boolean) {
  const size = selected ? 38 : 28;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 36 36">
    <g transform="rotate(${heading},18,18)">
      <polygon points="18,2 28,30 18,24 8,30" fill="${color}" stroke="white" stroke-width="2.5"/>
    </g>
    ${selected ? `<circle cx="18" cy="18" r="17" fill="none" stroke="${color}" stroke-width="2.5" opacity="0.6"/>` : ''}
  </svg>`;
  return L.divIcon({ html: svg, className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

// ── MSIL GeoJSON → Leaflet レイヤー変換 ───────────────────────────────────────
function buildMsilLayer(layerId: string, data: GeoJSON.FeatureCollection): L.Layer | null {
  if (!data?.features?.length) return null;

  switch (layerId) {
    case 'depth_contour':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        style: (f) => {
          const d = Number((f?.properties as Record<string, unknown>)?.['depth'] ?? 0);
          const color = d < 10 ? '#bfdbfe' : d < 30 ? '#93c5fd' : d < 100 ? '#3b82f6' : d < 500 ? '#1d4ed8' : '#1e3a8a';
          return { color, weight: 1, opacity: 0.75, fillOpacity: 0 };
        },
        onEachFeature: (f, layer) => {
          const d = (f?.properties as Record<string, unknown>)?.['depth'];
          if (d != null) layer.bindTooltip(`${d}m`, { permanent: false, className: 'msil-tip' });
        },
      });

    case 'light':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        pointToLayer: (f, latlng) => {
          const name = String((f?.properties as Record<string, unknown>)?.['名称'] ?? '');
          const icon = L.divIcon({
            html: '<svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="6" fill="#fbbf24" stroke="#d97706" stroke-width="1.5"/></svg>',
            className: '', iconSize: [14, 14], iconAnchor: [7, 7],
          });
          return L.marker(latlng, { icon })
            .bindTooltip(name || '灯台/灯浮標', { permanent: false, className: 'msil-tip' });
        },
      });

    case 'seabed_obs':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        pointToLayer: (_f, latlng) =>
          L.circleMarker(latlng, { radius: 3, color: '#dc2626', weight: 1.5, fillColor: '#fca5a5', fillOpacity: 0.8 })
            .bindTooltip('海底障害物', { permanent: false, className: 'msil-tip' }),
      });

    case 'anchor_berth':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        pointToLayer: (f, latlng) => {
          const name = String((f?.properties as Record<string, unknown>)?.['objnam'] ?? '');
          const icon = L.divIcon({
            html: '<svg width="16" height="16" viewBox="0 0 16 16"><text x="8" y="13" text-anchor="middle" font-size="13" fill="#1e40af">⚓</text></svg>',
            className: '', iconSize: [16, 16], iconAnchor: [8, 8],
          });
          return L.marker(latlng, { icon })
            .bindTooltip(name || '指定錨地', { permanent: false, className: 'msil-tip' });
        },
      });

    case 'traffic_route':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        style: () => ({ color: '#10b981', weight: 2, opacity: 0.9, fillColor: '#10b981', fillOpacity: 0.08 }),
        onEachFeature: (f, layer) => {
          const name = String((f?.properties as Record<string, unknown>)?.['航路名'] ?? '');
          if (name) layer.bindTooltip(`海交法：${name}`, { permanent: false, className: 'msil-tip' });
        },
      });

    case 'port_passage':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        style: () => ({ color: '#06b6d4', weight: 2, opacity: 0.9, fillColor: '#06b6d4', fillOpacity: 0.08 }),
        onEachFeature: (f, layer) => {
          const name = String((f?.properties as Record<string, unknown>)?.['航路名'] ?? '');
          const port = String((f?.properties as Record<string, unknown>)?.['港名'] ?? '');
          if (name || port) layer.bindTooltip(`港則法：${name}（${port}）`, { permanent: false, className: 'msil-tip' });
        },
      });

    case 'port_area':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        style: () => ({ color: '#8b5cf6', weight: 1.5, opacity: 0.7, fillColor: '#8b5cf6', fillOpacity: 0.06 }),
        onEachFeature: (f, layer) => {
          const name = String((f?.properties as Record<string, unknown>)?.['港名'] ?? '');
          if (name) layer.bindTooltip(name, { permanent: false, className: 'msil-tip' });
        },
      });

    case 'nav_warning':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        pointToLayer: (f, latlng) => {
          const icon = L.divIcon({
            html: '<svg width="18" height="18" viewBox="0 0 18 18"><polygon points="9,1 17,17 1,17" fill="#f59e0b" stroke="#92400e" stroke-width="1.5"/><text x="9" y="14.5" text-anchor="middle" font-size="8" fill="white" font-weight="bold">!</text></svg>',
            className: '', iconSize: [18, 18], iconAnchor: [9, 9],
          });
          const name  = String((f?.properties as Record<string, unknown>)?.['name'] ?? '');
          const desc  = String((f?.properties as Record<string, unknown>)?.['description'] ?? '');
          return L.marker(latlng, { icon })
            .bindTooltip(name || desc || '航行警報', { permanent: false, className: 'msil-tip' });
        },
      });

    case 'notices':
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        pointToLayer: (_f, latlng) => {
          const icon = L.divIcon({
            html: '<svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5.5" fill="#7c3aed" stroke="#5b21b6" stroke-width="1"/><text x="6" y="9.5" text-anchor="middle" font-size="7" fill="white" font-weight="bold">水</text></svg>',
            className: '', iconSize: [12, 12], iconAnchor: [6, 6],
          });
          return L.marker(latlng, { icon })
            .bindTooltip('水路通報', { permanent: false, className: 'msil-tip' });
        },
      });

    case 'tidal_isewan':
    case 'tidal_seto':
    case 'tidal_tokyo': {
      const areaLabel = layerId === 'tidal_isewan' ? '伊勢湾' : layerId === 'tidal_seto' ? '瀬戸内海' : '東京湾';
      return L.geoJSON(data as GeoJSON.GeoJsonObject, {
        pointToLayer: (f, latlng) => {
          const dir = Number((f?.properties as Record<string, unknown>)?.['currentdirection'] ?? 0);
          const spd = Number((f?.properties as Record<string, unknown>)?.['currentspeedkt'] ?? 0);
          const icon = L.divIcon({
            html: `<svg width="24" height="24" viewBox="0 0 24 24"><g transform="rotate(${dir},12,12)"><polygon points="12,2 15,16 12,12 9,16" fill="#0891b2" stroke="#0e7490" stroke-width="1"/><line x1="12" y1="16" x2="12" y2="22" stroke="#0891b2" stroke-width="1.5"/></g></svg>`,
            className: '', iconSize: [24, 24], iconAnchor: [12, 12],
          });
          return L.marker(latlng, { icon })
            .bindTooltip(`${areaLabel} ${spd.toFixed(1)} kt ${dir}°`, { permanent: false, className: 'msil-tip' });
        },
      });
    }
  }
  return null;
}

// ── レイヤー管理コンポーネント ──────────────────────────────────────────────────
function MapLayers({ mapType, showMaritimeZones, apiBase, msilLayers }: {
  mapType: MapType;
  showMaritimeZones: boolean;
  apiBase: string;
  msilLayers: string[];
}) {
  const map          = useMap();
  const baseRef      = useRef<L.Layer | null>(null);
  const overlaysRef  = useRef<L.Layer[]>([]);
  const zonesRef     = useRef<L.GeoJSON | null>(null);
  const msilRefs     = useRef<Map<string, L.Layer>>(new Map());

  // ベースタイル切り替え
  useEffect(() => {
    if (baseRef.current) { map.removeLayer(baseRef.current); baseRef.current = null; }

    if (mapType === 'satellite') {
      const tile = createBaseLayer('satellite');
      tile.addTo(map);
      baseRef.current = tile;
    } else {
      // 標準: ベースマップ（会社フラグで自社配信/外部タイルが切り替わる）+ 灯台・航行標識
      const base = createBaseLayer('standard');
      base.addTo(map);
      baseRef.current = base;
      const seamark = createSeamarkLayer();
      if (seamark) {
        seamark.addTo(map);
        overlaysRef.current = [seamark];
      }
    }

    return () => {
      if (baseRef.current) { map.removeLayer(baseRef.current); baseRef.current = null; }
      overlaysRef.current.forEach(l => map.removeLayer(l));
      overlaysRef.current = [];
    };
  }, [mapType, map]);

  // 海域区分 GeoJSON レイヤー
  useEffect(() => {
    if (zonesRef.current) { map.removeLayer(zonesRef.current); zonesRef.current = null; }
    if (!showMaritimeZones || !apiBase) return;

    const ZONE_STYLES: Record<string, L.PathOptions> = {
      '沿海区域': { color: '#3b82f6', weight: 4, fillOpacity: 0.07, dashArray: undefined, opacity: 1.0 },
      '接続水域': { color: '#f59e0b', weight: 4, fillOpacity: 0.05, dashArray: '10 6',    opacity: 1.0 },
    };

    fetch(`${apiBase}/api/geo/maritime-zones`)
      .then(r => r.json())
      .then((data: GeoJSON.FeatureCollection) => {
        if (!data?.features?.length) return;
        const layer = L.geoJSON(data as GeoJSON.GeoJsonObject, {
          style: (feature) => {
            const zone = (feature?.properties as Record<string, unknown> | null)?.['zone'] as string | undefined;
            return ZONE_STYLES[zone ?? ''] ?? { color: '#6366f1', weight: 3, fillOpacity: 0.05, opacity: 0.9 };
          },
        });
        layer.addTo(map);
        zonesRef.current = layer;
      })
      .catch(err => console.warn('maritime-zones fetch failed:', err));

    return () => {
      if (zonesRef.current) { map.removeLayer(zonesRef.current); zonesRef.current = null; }
    };
  }, [showMaritimeZones, apiBase, map]);

  // MSIL レイヤーの追加/削除
  const msilKey = msilLayers.slice().sort().join(',');
  useEffect(() => {
    if (!apiBase) return;

    // 削除：現在表示中だが新しい msilLayers にないもの
    msilRefs.current.forEach((layer, id) => {
      if (!msilLayers.includes(id)) {
        map.removeLayer(layer);
        msilRefs.current.delete(id);
      }
    });

    // 追加：新しく追加されたレイヤー
    for (const layerId of msilLayers) {
      if (msilRefs.current.has(layerId)) continue;
      fetch(`${apiBase}/api/geo/msil-layer/${layerId}`)
        .then(r => r.json())
        .then((data: GeoJSON.FeatureCollection) => {
          // エフェクトが再実行されて既に消された可能性があるのでチェック
          if (!msilLayers.includes(layerId)) return;
          const layer = buildMsilLayer(layerId, data);
          if (layer) {
            // 出典は地図右下の attribution に出す。同じ文字列なので複数レイヤーでも1つにまとまり、
            // 全レイヤーを消したときに Leaflet 側で自動的に消える。
            if (hasFeature('msil_attribution')) {
              (layer.options as L.LayerOptions).attribution = MSIL_ATTRIBUTION;
            }
            layer.addTo(map);
            msilRefs.current.set(layerId, layer);
          }
        })
        .catch(err => console.warn(`msil-layer ${layerId} error:`, err));
    }

    return () => {
      msilRefs.current.forEach(layer => map.removeLayer(layer));
      msilRefs.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msilKey, apiBase, map]);

  return null;
}

// ── 寄港地マーカーレイヤー ─────────────────────────────────────────────────────
function PortsLayer({ ports }: { ports: PortMarker[] }) {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    if (!ports.length) return;

    const portIcon = L.divIcon({
      html: '<svg width="22" height="28" viewBox="0 0 22 28"><path d="M11 0C4.9 0 0 4.9 0 11c0 8.3 11 17 11 17s11-8.7 11-17C22 4.9 17.1 0 11 0z" fill="#0891b2" stroke="white" stroke-width="1.5"/><text x="11" y="15" text-anchor="middle" font-size="11" fill="white">⚓</text></svg>',
      className: '',
      iconSize: [22, 28],
      iconAnchor: [11, 28],
    });

    const group = L.layerGroup();
    ports.forEach(p => {
      const label = p.code ? `${p.name}（${p.code}）` : p.name;
      const popup = `<div style="font-family:sans-serif;min-width:120px"><div style="font-weight:700;font-size:13px;margin-bottom:4px">${label}</div>${p.description ? `<div style="font-size:11px;color:#555">${p.description}</div>` : ''}</div>`;
      L.marker([p.latitude, p.longitude], { icon: portIcon })
        .bindPopup(popup)
        .bindTooltip(p.name, { permanent: false, className: 'msil-tip' })
        .addTo(group);
    });
    group.addTo(map);
    layerRef.current = group;

    return () => {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
    };
  }, [ports, map]);

  return null;
}

// ── 選択船舶にフライ ────────────────────────────────────────────────────────────
function FlyToSelected({ vessels, selectedId, currentTime }: {
  vessels: VesselData[]; selectedId: string | null; currentTime: number;
}) {
  const map    = useMap();
  const prevId = useRef<string | null>(null);
  useEffect(() => {
    if (selectedId === null) return;
    if (prevId.current === selectedId) return;
    prevId.current = selectedId;
    const v = vessels.find(v => v.id === selectedId);
    if (!v) return;
    const pos = interpolatePosition(v.trail, currentTime);
    if (pos) map.flyTo([pos.lat, pos.lng], 7, { duration: 1.2 });
  }, [selectedId, vessels, currentTime, map]);
  return null;
}

// ── メインコンポーネント ────────────────────────────────────────────────────────
const VesselTrailMap: React.FC<Props> = ({
  vessels, selectedId, currentTime, showTrail, showLabel,
  mapType = 'standard', showMaritimeZones = false, apiBase = '',
  msilLayers = [], ports = [], hideUi = false,
}) => {
  return (
    <>
    <style>{`
      .vessel-name-label {
        background: rgba(255,255,255,0.95) !important;
        border-radius: 4px !important;
        padding: 2px 7px !important;
        border: none !important;
        box-shadow: 0 1px 4px rgba(0,0,0,0.22) !important;
      }
      .vessel-name-label::before { display: none !important; }
      .msil-tip {
        background: rgba(15,23,42,0.88) !important;
        border: none !important;
        border-radius: 5px !important;
        color: #f1f5f9 !important;
        font-size: 11px !important;
        font-weight: 600 !important;
        padding: 3px 8px !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.35) !important;
        white-space: nowrap !important;
      }
      .msil-tip::before { display: none !important; }
      .vt-hide-ui .leaflet-control-container { display: none !important; }
    `}</style>
    <div className={hideUi ? 'vt-hide-ui' : undefined} style={{ position: 'relative', width: '100%', height: '100%' }}>
    <MapContainer
      center={[34.5, 136.0]}
      zoom={5}
      style={{ width: '100%', height: '100%', background: '#e8f4f8' }}
      zoomControl={true}
      worldCopyJump={false}
    >
      <MapLayers
        mapType={mapType}
        showMaritimeZones={showMaritimeZones}
        apiBase={apiBase}
        msilLayers={msilLayers}
      />
      <FlyToSelected vessels={vessels} selectedId={selectedId} currentTime={currentTime} />
      {ports.length > 0 && <PortsLayer ports={ports} />}

      {vessels.map(vessel => {
        const pos = interpolatePosition(vessel.trail, currentTime);
        if (!pos) return null;
        const isSelected = selectedId === vessel.id;
        const color = vessel.status === 'offline' ? STATUS_COLOR.offline : vessel.color;
        const trailUpToNow = vessel.trail
          .filter(p => p.ts <= currentTime)
          .sort((a, b) => a.ts - b.ts)
          .map(p => [p.lat, p.lng] as [number, number]);

        return (
          <React.Fragment key={vessel.id}>
            {showTrail && trailUpToNow.length >= 2 && (
              <Polyline
                positions={trailUpToNow}
                pathOptions={{
                  color, weight: isSelected ? 3 : 1.5,
                  opacity: isSelected ? 0.9 : 0.45,
                  dashArray: vessel.status === 'offline' ? '6,6' : undefined,
                }}
              />
            )}
            <Marker
              position={[pos.lat, pos.lng]}
              icon={createShipIcon(color, pos.heading, isSelected)}
            >
              <Popup>
                <div style={{ fontFamily: 'sans-serif', minWidth: '140px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px' }}>{vessel.name}</div>
                  <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{
                      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                      background: color + '22', color, fontWeight: 700,
                    }}>
                      {STATUS_LABEL[vessel.status] ?? vessel.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>コード: {vessel.code || '-'}</div>
                  <div style={{ fontSize: '12px', color: '#555' }}>
                    速度: {vessel.trail.find(p => p.ts <= currentTime)?.speed?.toFixed(1) ?? '-'} kt
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>針路: {pos.heading}°</div>
                  <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>
                    {pos.lat.toFixed(4)}°N, {pos.lng.toFixed(4)}°E
                  </div>
                </div>
              </Popup>
              {showLabel && (
                <Tooltip
                  permanent direction="top"
                  offset={[0, -(isSelected ? 23 : 18)]}
                  opacity={1} className="vessel-name-label"
                >
                  <span style={{ color, fontWeight: 700, fontSize: isSelected ? 12 : 11, whiteSpace: 'nowrap' }}>
                    {vessel.name}
                  </span>
                </Tooltip>
              )}
            </Marker>
          </React.Fragment>
        );
      })}
    </MapContainer>

    {/* 運航状態 凡例（打刻・GPSからの自動判定） */}
    {!hideUi && (
    <div style={{
      position: 'absolute', bottom: showMaritimeZones ? 116 : 28, right: 10, zIndex: 1000,
      background: 'rgba(255,255,255,0.93)',
      borderRadius: 8, padding: '8px 12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      fontSize: 12, fontWeight: 600, lineHeight: 1,
      pointerEvents: 'none',
    }}>
      <div style={{ marginBottom: 6, fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.04em' }}>運航状態（自動判定）</div>
      {(['sailing', 'port', 'anchored', 'offline'] as const).map((key, i) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 3 ? 5 : 0 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLOR[key], display: 'inline-block' }} />
          <span style={{ color: '#374151' }}>{STATUS_LABEL[key]}</span>
        </div>
      ))}
    </div>
    )}

    {/* 海域区分 凡例 */}
    {showMaritimeZones && !hideUi && (
      <div style={{
        position: 'absolute', bottom: 28, right: 10, zIndex: 1000,
        background: 'rgba(255,255,255,0.93)',
        borderRadius: 8, padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        fontSize: 12, fontWeight: 600, lineHeight: 1,
        pointerEvents: 'none',
      }}>
        <div style={{ marginBottom: 6, fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.04em' }}>海域区分</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
          <svg width="32" height="6">
            <line x1="0" y1="3" x2="32" y2="3" stroke="#3b82f6" strokeWidth="3"/>
          </svg>
          <span style={{ color: '#1e40af' }}>沿海区域（20海里）</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="32" height="6">
            <line x1="0" y1="3" x2="32" y2="3" stroke="#f59e0b" strokeWidth="3" strokeDasharray="6 4"/>
          </svg>
          <span style={{ color: '#92400e' }}>接続水域（24海里）</span>
        </div>
      </div>
    )}
    </div>
    </>
  );
};

export default VesselTrailMap;
